import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EventService } from './event.service';
import { EncryptionService } from './encryption.service';

/**
 * Opens an event's "Edit Event Details" panel (Manage Event, ?panel=details).
 *
 * The one way into editing for the places that offer "Edit details": the event
 * page, the invite screen right after creating, and the create form's "You
 * already have this event" prompt. Hosts were fixing a typo by creating the
 * event again (one wedding ended up as three events, 2026-10-20) because the
 * only way in was Manage Event, behind the cover's settings menu.
 *
 * Manage Event reads the event from localStorage — 'eventId' (the hex) and an
 * encrypted 'inviteData' from pwa_get_media_count — so this writes both first,
 * exactly as event-detail's openEventDetails() does.
 */
@Injectable({ providedIn: 'root' })
export class EventEditLauncherService {

  /** Manage Event is the only editor this opens; without it there is nothing to offer. */
  readonly enabled: boolean = environment.features.manageEventHub;

  constructor(
    private router: Router,
    private eventService: EventService,
    private encryptionService: EncryptionService,
  ) { }

  /** Resolves false when the event couldn't be loaded, so the caller can say so. */
  async openDetails(eventHex: string): Promise<boolean> {
    if (!this.enabled || !eventHex) return false;
    localStorage.setItem('eventId', eventHex);
    try {
      const res = await firstValueFrom(this.eventService.getTotalMediaCountFresh(eventHex));
      if (!res) return false;
      const encrypted = this.encryptionService.encryptData(JSON.stringify(res));
      localStorage.setItem('invite-info', encrypted);
      localStorage.setItem('inviteData', encrypted);
      return await this.router.navigate(['/manage-event'], { queryParams: { panel: 'details' } });
    } catch {
      return false;
    }
  }
}
